import { FC, ReactNode, useCallback } from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { areUserPreferencesInError } from 'state/selectors';
import { userPreferencesDefaulted, userPreferencesRequested } from 'state/userPreferences/userPreferences.action';

interface Props {
  children: ReactNode;
  hasLoadingError: boolean;
  onContinueAndDefault(): void;
}

const Component: FC<Props> = ({ hasLoadingError, onContinueAndDefault, children }) => {
  const { formatMessage } = useIntl();

  const dispatch = useDispatch();
  const onRefreshed = useCallback(() => dispatch(userPreferencesRequested()), [dispatch]);

  return (
    <>
      {children}
      <Modal show={hasLoadingError}>
        <ModalHeader>
          <ModalTitle data-e2e="user-preferences-loading-error-modal-title" as="h3">
            {formatMessage({ id: 'userPreferences.loadingErrorModal.title' })}
          </ModalTitle>
        </ModalHeader>
        <ModalBody>
          <span data-e2e="user-preferences-loading-error-modal-text">
            {formatMessage({ id: 'userPreferences.loadingErrorModal.text' })}
          </span>
        </ModalBody>
        <ModalFooter className="justify-content-start">
          <Button
            variant="secondary"
            size="lg"
            onClick={onRefreshed}
            data-e2e="user-preferences-loading-error-modal-refresh-btn"
          >
            {formatMessage({ id: 'userPreferences.loadingErrorModal.button.refresh' })}
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={onContinueAndDefault}
            data-e2e="user-preferences-loading-error-modal-continue-btn"
          >
            {formatMessage({ id: 'userPreferences.loadingErrorModal.button.continue' })}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};
Component.displayName = 'UserPreferencesLoadingErrorModal';

export const UserPreferencesLoadingErrorModal: FC<{ children: ReactNode }> = ({ children }) => {
  const dispatch = useDispatch();

  const hasLoadingError = useSelector(areUserPreferencesInError);
  const onContinueAndDefault = useCallback(() => dispatch(userPreferencesDefaulted()), [dispatch]);

  return (
    <Component hasLoadingError={hasLoadingError} onContinueAndDefault={onContinueAndDefault}>
      {children}
    </Component>
  );
};
